// Copyright 2026, Auxilio Technologies LLC

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getAssignableRoles, RoleType, getRoleInfo } from '../config/rolesConfig';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface ManagedUser {
  id: string;
  email: string;
  username: string;
  role: string;
  is_active: boolean;
  last_login?: string;
}

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const myRole = (user?.role || 'viewer') as RoleType;
  const assignable = getAssignableRoles(myRole);

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>(assignable[assignable.length - 1] || 'florist');
  const [notice, setNotice] = useState('');

  const load = () => {
    setLoading(true);
    authAPI
      .listUsers()
      .then((res) => setUsers(res.data.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await authAPI.inviteUser({ email: inviteEmail, role: inviteRole });
      setNotice(t('users.invited'));
      setInviteEmail('');
      setShowInvite(false);
    } catch (err: any) {
      setNotice(err.response?.data?.detail || t('auth.errorGeneric'));
    }
  };

  const changeRole = async (u: ManagedUser, role: string) => {
    await authAPI.updateUser(u.id, { role });
    load();
  };

  const toggleActive = async (u: ManagedUser) => {
    await authAPI.updateUser(u.id, { is_active: !u.is_active });
    load();
  };

  const remove = async (u: ManagedUser) => {
    await authAPI.deleteUser(u.id);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('users.title')}</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">{t('users.subtitle')}</p>
        </div>
        <button onClick={() => setShowInvite((v) => !v)} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">
          <PlusIcon className="h-5 w-5" />
          {t('users.invite')}
        </button>
      </div>

      {notice && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">{notice}</div>}

      {showInvite && (
        <form onSubmit={invite} className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('users.inviteEmail')}</label>
            <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('users.inviteRole')}</label>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-rose-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              {assignable.map((r) => (
                <option key={r} value={r}>{t(getRoleInfo(r).nameKey)}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">{t('users.send')}</button>
        </form>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? (
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
        ) : users.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">{t('users.empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-400 dark:bg-gray-900/40">
              <tr>
                <th className="px-6 py-3">{t('users.email')}</th>
                <th className="px-6 py-3">{t('users.username')}</th>
                <th className="px-6 py-3">{t('users.role')}</th>
                <th className="px-6 py-3">{t('users.status')}</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((u) => {
                const isSelf = u.id === user?.id;
                const canEdit = !isSelf && assignable.includes(u.role as RoleType);
                return (
                  <tr key={u.id} className="text-gray-700 dark:text-gray-300">
                    <td className="px-6 py-3">{u.email}</td>
                    <td className="px-6 py-3">{u.username}</td>
                    <td className="px-6 py-3">
                      {canEdit ? (
                        <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                          {[u.role, ...assignable.filter((r) => r !== u.role)].map((r) => (
                            <option key={r} value={r}>{t(getRoleInfo(r as RoleType).nameKey)}</option>
                          ))}
                        </select>
                      ) : (
                        t(getRoleInfo(u.role as RoleType).nameKey)
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                        {u.is_active ? t('users.active') : t('users.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {canEdit && (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => toggleActive(u)} className="text-xs text-gray-500 hover:text-rose-600 dark:text-gray-400">
                            {u.is_active ? t('users.deactivate') : t('users.activate')}
                          </button>
                          <button onClick={() => remove(u)} title={t('users.delete')} className="text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
